import * as consts from './pattern-editor-constants'
import { fabric } from 'fabric'
import { BoxDef, fullHeight, fullWidth } from './boxDef'

export class FigureBuilder {

  createObjectBoxContents (width: number, height: number, boxDef: BoxDef, orderNum?: number, marginRect: boolean = true,
                           groupName: string = '', showBoxLabel: boolean = true):
    fabric.Object[] {
    const arr: fabric.Object[] = []

    // marginRect not rendered for template boxes
    if (marginRect) {
      const marginOptions: fabric.IObjectOptions = {
        left: 0, top: 0, width: fullWidth(boxDef), height: fullHeight(boxDef),
        stroke: '#0000ff', strokeDashArray: [5, 10], strokeWidth: 1,
        fill: 'transparent', originX: 'center', originY: 'center'
      }
      const margin = new fabric.Rect(marginOptions)
      arr.push(margin)
    }

    const boxOptions: fabric.IObjectOptions = {
      left: 0,
      top: 0,
      width: width - consts.boxBorderWidth,
      height: height - consts.boxBorderWidth,
      stroke: consts.boxBorderColor,
      strokeWidth: 14,
      fill: consts.boxColor,
      originX: 'center',
      originY: 'center',
    }
    const box = new fabric.Rect(boxOptions)
    arr.push(box)

    const highlightOption: fabric.IObjectOptions = {
      left: 0, top: 0, originX: 'center', originY: 'center',
      width: width, height: height, fill: consts.badPlaceColor, opacity: 0, name: 'highlight'
    }
    const highlight = new fabric.Rect(highlightOption)
    arr.push(highlight)

    const blurOptions: fabric.IObjectOptions = {
      left: 0, top: 0, originX: 'center', originY: 'center',
      width: width, height: height, backgroundColor: '#909090', opacity: 0, name: 'blur'
    }
    const blur = new fabric.Rect(blurOptions)
    arr.push(blur)

    const tapeOptions: fabric.IObjectOptions = {
      left: 0, top: 0, width: (width > height) ? width : 2, height: (width > height) ? 2 : height,
      fill: consts.tapeColor, originX: 'center', originY: 'center'
    }
    const tape = new fabric.Rect(tapeOptions)
    arr.push(tape)

    const label = this.makeLabel(width, height, boxDef.labelPos)
    label.set({ opacity: showBoxLabel ? 1 : 0 })
    arr.push(label)

    const cross = this.makeCross(width, height)
    arr.push(cross)

    const text = this.makeText(orderNum)
    arr.push(text)

    const groupText = this.makeGroupText(groupName, width, height)
    arr.push(groupText)

    const error = this.makeError(width, height)
    arr.push(error)

    const warning = this.makeWarning(width, height)
    arr.push(warning)

    const arrow = this.makeArrow(width, height)
    arr.push(arrow)

    const warningArrow = this.makeWarningArrow(width, height)
    arr.push(warningArrow)

    return arr
  }

  makeTextVertical (box: fabric.Group) {
    if (box.name === 'box') {
      box.getObjects()
        .filter(p => p.name === 'label' || p.name === 'group')
        .forEach(obj => obj.set('angle', -box.angle! % 360))
    }
  }

  updateBoxLabel (box: fabric.Group, width: number, height: number, labelPos: string, visible: boolean) {
    const existingLabel = box.getObjects().find(p => p.name === 'box-label')
    if (!visible) {
      if (existingLabel) {
        existingLabel.set({ opacity: 0 })
        existingLabel.dirty = true
      }
      return
    }
    const label = this.makeLabel(width, height, labelPos)
    label.set({ opacity: 1 })
    if (existingLabel) {
      const objects = box.getObjects()
      const index = objects.indexOf(existingLabel)
      if (index >= 0) {
        // Fabric 2 recalculates parent bounds when using remove/add helpers, which can move the box.
        // Replace only the label child so the parent box geometry stays stable while the label moves.
        // @ts-ignore Fabric 2 keeps grouped children on the private _objects array.
        box._objects.splice(index, 1, label)
        label.group = box
        label.canvas = box.canvas
        box.dirty = true
      }
      return
    }
    box.addWithUpdate(label)
  }

  makeCross (width: number, height: number): fabric.Group {
    const shorten = consts.redCrossScale
    const crossOpt = {
      fill: consts.badPlaceColor,
      stroke: consts.badPlaceColor,
      strokeWidth: width / 25,
      originX: 'center',
      originY: 'center'
    }
    const line1 = new fabric.Line([ -width * shorten, -height * shorten, width * shorten, height * shorten ], crossOpt)
    const line2 = new fabric.Line([ width * shorten, -height * shorten, -width * shorten, height * shorten ], crossOpt)
    return new fabric.Group([line1, line2], {name: 'cross', opacity: 0})
  }

  makeError(width: number, height: number) {
    const size = Math.min(width, height)
    const fontScale = (size > 120) ? 2 : 1.2
    const bg = new fabric.Rect({
      left: 0,
      top: 0,
      width: size,
      height: size,
      opacity: 0
    })
    const text = new fabric.Text('!', {
      fill: consts.badPlaceColor,
      fontFamily: 'Arial',
      left: size - consts.boxBorderWidth * 2,
      top: consts.boxBorderWidth,
      name: 'error',
      fontSize: consts.boxLabel.fontSize * fontScale,
      fontWeight: consts.boxLabel.fontWeight,
    })
    return new fabric.Group([bg, text], {
      width: size,
      height: size,
      left: 0,
      top: 0,
      originX: 'center',
      originY: 'center',
      name: 'error',
      opacity: 0
    })
  }

  makeWarning(width: number, height: number) {
    const size = Math.min(width, height)
    const fontScale = (size > 120) ? 2 : 1.2
    const bg = new fabric.Rect({
      left: 0,
      top: 0,
      width: size,
      height: size,
      opacity: 0
    })
    const text = new fabric.Text('!', {
      fill: consts.warningColor,
      fontFamily: 'Arial',
      left: size - consts.boxBorderWidth * 2,
      top: consts.boxBorderWidth,
      name: 'warning',
      fontSize: consts.boxLabel.fontSize * fontScale,
      fontWeight: consts.boxLabel.fontWeight,
    })
    return new fabric.Group([bg, text], {
      width: size,
      height: size,
      left: 0,
      top: 0,
      originX: 'center',
      originY: 'center',
      name: 'warning',
      opacity: 0
    })
  }

  makeGroupBorders(points: Array<{ x: number, y: number, pusher?: boolean, doublePlace?: boolean }>): fabric.Object[] {
    const strokeColor = points[0].pusher ?  consts.pusherGroupBorder : consts.groupBorder
    const options: fabric.IObjectOptions = {
      name: 'pushergroup',
      selectable: false,
      stroke: strokeColor,
      strokeWidth: 5,
    }
    const polys = []
    for (let i = 0; i < points.length; i++) {
      const nextIndex = (i === points.length - 1) ? 0 : i + 1
      polys.push(new fabric.Polygon([points[i], points[nextIndex]], options))
    }
    return polys
  }

  makePalletOverhang (width: number, height: number): fabric.Object {
    const options: fabric.IObjectOptions = {
      left: 0, top: 0, width: width, height: height,
      strokeWidth: 1, fill: 'transparent', stroke: 'red', strokeDashArray: [5, 5],
      name: 'background.overhang'
    }
    return new fabric.Rect(options)
  }

  makeReferenceFrame (width: number, height: number, originLeft: number, originTop: number): fabric.Group {
    const stroke = 6
    const arrowSize = 30
    const refColor = 'red'
    const elements = []

    const bg = new fabric.Rect({
      left: -25,
      top: -44,
      width: width + arrowSize + 20,
      height: height + 90,
      selectable: false,
      opacity: 0
    })
    elements.push(bg)
    const optionsX: fabric.IObjectOptions = {
      left: width * 0.5,
      top: 0,
      stroke: refColor,
      strokeWidth: stroke
    }
    const xAxis = new fabric.Line([ width * 0.5, 0, width, 0 ], optionsX)
    elements.push(xAxis)
    const xTriangle = new fabric.Triangle({
      width: arrowSize,
      height: arrowSize,
      fill: refColor,
      left: width * 0.5 - stroke,
      top: (arrowSize * 0.5 + stroke * 0.5),
      angle: 270
    })
    elements.push(xTriangle)
    const xTitle = new fabric.Text('x', {
      fill: refColor,
      fontFamily: 'Arial',
      left: width * 0.5 - arrowSize,
      top: -(arrowSize + 10),
      name: 'xAxisTitle',
      fontSize: consts.boxLabel.fontSize,
      fontWeight: consts.boxLabel.fontWeight
    })
    elements.push(xTitle)

    const optionsY: fabric.IObjectOptions = {
      left: width,
      top: 0,
      stroke: refColor,
      strokeWidth: stroke
    }
    const yAxis = new fabric.Line([ width, 0, width, height * 0.5 ], optionsY)
    elements.push(yAxis)
    const yTriangle = new fabric.Triangle({
      width: arrowSize,
      height: arrowSize,
      fill: refColor,
      left: width + arrowSize * 0.5 + stroke * 0.5,
      top: height * 0.5 + stroke,
      angle: 180
    })
    elements.push(yTriangle)
    const yTitle = new fabric.Text('y', {
      fill: refColor,
      fontFamily: 'Arial',
      left: width + arrowSize * 0.5,
      top: height * 0.5 + stroke,
      name: 'yAxisTitle',
      fontSize: consts.boxLabel.fontSize,
      fontWeight: consts.boxLabel.fontWeight
    })
    elements.push(yTitle)

    const groupOptions: fabric.IObjectOptions = {
      left: originLeft,
      top: originTop,
      originX: 'center',
      originY: 'center',
      centeredRotation: true,
      name: 'reference',
      hasControls: false,
      lockMovementX: false,
      lockMovementY: false,
      selectable: false
    }
    return new fabric.Group(elements, groupOptions)
  }

  makeGripperBorders(points: Array<{ x: number, y: number }>, overlay: boolean = false): fabric.Group {
    const options: fabric.IObjectOptions = {
      selectable: false,
      stroke: '#00AAFF',
      strokeWidth: 6
    }
    const polys = []
    for (let i = 0; i < points.length; i++) {
      const nextIndex = (i === points.length - 1) ? 0 : i + 1
      polys.push(new fabric.Polygon([points[i], points[nextIndex]], options))
    }

    let opt: fabric.IObjectOptions = {
      name: 'gripper-border',
      selectable: false,
      evented: false
    }
    if (overlay) {
      opt = {
        ...opt,
        centeredRotation: true,
        originX: 'center',
        originY: 'center'
      }
    }
    if (overlay) {
      opt = {
        ...opt,
        centeredRotation: true,
        originX: 'center',
        originY: 'center'
      }
    }
    return new fabric.Group(polys, opt)
  }

  makeGripperTcpReferenceFrame (centerX: number, centerY: number, width: number, height: number, rotation: number = 0): fabric.Object[] {
    const stroke = 6
    const arrowSize = 30
    const refColor = 'red'
    const axisLength = Math.max(Math.min(width, height) * 0.35, arrowSize * 2)
    const options: fabric.IObjectOptions = {
      selectable: false,
      evented: false,
      name: 'gripper-border'
    }
    const lineOptions: fabric.IObjectOptions = {
      ...options,
      stroke: refColor,
      strokeWidth: stroke
    }
    const labelOptions: fabric.ITextOptions = {
      ...options,
      fill: refColor,
      fontFamily: 'Arial',
      fontSize: consts.boxLabel.fontSize,
      fontWeight: consts.boxLabel.fontWeight
    }

    const angle = rotation / 180 * Math.PI
    const xVector = {
      x: -Math.cos(angle) * axisLength,
      y: Math.sin(angle) * axisLength
    }
    const yVector = {
      x: Math.sin(angle) * axisLength,
      y: Math.cos(angle) * axisLength
    }
    const xEnd = { x: centerX + xVector.x, y: centerY + xVector.y }
    const yEnd = { x: centerX + yVector.x, y: centerY + yVector.y }

    return [
      new fabric.Line([centerX, centerY, xEnd.x, xEnd.y], lineOptions),
      new fabric.Triangle({
        ...options,
        width: arrowSize,
        height: arrowSize,
        fill: refColor,
        left: xEnd.x,
        top: xEnd.y,
        originX: 'center',
        originY: 'center',
        angle: 270 - rotation
      }),
      new fabric.Text('x', {
        ...labelOptions,
        left: xEnd.x - arrowSize,
        top: xEnd.y - arrowSize - 10
      }),
      new fabric.Line([centerX, centerY, yEnd.x, yEnd.y], lineOptions),
      new fabric.Triangle({
        ...options,
        width: arrowSize,
        height: arrowSize,
        fill: refColor,
        left: yEnd.x,
        top: yEnd.y,
        originX: 'center',
        originY: 'center',
        angle: 180 - rotation
      }),
      new fabric.Text('y', {
        ...labelOptions,
        left: yEnd.x + arrowSize * 0.5,
        top: yEnd.y
      }),
      new fabric.Circle({
        ...options,
        left: centerX,
        top: centerY,
        radius: stroke * 1.5,
        originX: 'center',
        originY: 'center',
        fill: refColor,
        stroke: 'white',
        strokeWidth: 2
      })
    ]
  }

  makeArrow(width: number, height: number) {
    const size = Math.min (width, height)
    const fontScale = (size > 120) ? 2 : 1.2
    const bg = new fabric.Rect ({
      left: 0,
      top: 0,
      width: size,
      height: size,
      opacity: 0
    })
    const text = new fabric.Text ('↷', {
      fill: consts.badPlaceColor,
      fontFamily: 'Arial',
      left: size / 2 - consts.boxBorderWidth * 2,
      top: 0,
      name: 'rotation',
      fontSize: consts.boxLabel.fontSize * fontScale,
      fontWeight: consts.boxLabel.fontWeight,
    })
    return new fabric.Group ([bg, text], {
      width: size,
      height: size,
      left: 0,
      top: 0,
      originX: 'center',
      originY: 'center',
      name: 'rotation',
      opacity: 0
    })
  }

  makeWarningArrow(width: number, height: number) {
    const size = Math.min(width, height)
    const fontScale = (size > 120) ? 2 : 1.2
    const bg = new fabric.Rect({
      left: 0,
      top: 0,
      width: size,
      height: size,
      opacity: 0
    })
    const text = new fabric.Text('↷', {
      fill: consts.warningColor,
      fontFamily: 'Arial',
      left: size / 2 - consts.boxBorderWidth * 2,
      top: 0,
      name: 'warningArrow',
      fontSize: consts.boxLabel.fontSize * fontScale,
      fontWeight: consts.boxLabel.fontWeight,
    })
    return new fabric.Group([bg, text], {
      width: size,
      height: size,
      left: 0,
      top: 0,
      originX: 'center',
      originY: 'center',
      name: 'warningArrow',
      opacity: 0
    })
  }

  // robot movement numbers
  // noinspection JSMethodCanBeStatic
  private makeText(orderNum?: number): fabric.Object {
    // console.log('[DEBUG] order num label', orderNum)
    const labelText = (orderNum && orderNum > 0) ? `${orderNum}` : ''
    return new fabric.Text(labelText, {
      stroke: consts.idStrokeColor,
      fill: consts.idFillColor,
      fontFamily: 'Arial',
      originX: 'center',
      originY: 'center',
      name: 'label',
      fontSize: consts.boxLabel.fontSize,
      fontWeight: consts.boxLabel.fontWeight
    })
  }

  // pick and place numbers
  // noinspection JSMethodCanBeStatic
  private makeGroupText(groupName: string = 'X', width: number, height: number): fabric.Object {
    const size = Math.min(width, height)
    const bg = new fabric.Rect({
      left: 0,
      top: 0,
      width: size,
      height: size,
      opacity: 0
    })
    const text = new fabric.Text(groupName, {
      // stroke: consts.multiPickBorder, // consts.idStrokeColor,
      fill: consts.groupMarkerColor, // consts.idFillColor,
      fontFamily: 'Arial',
      // originX: 'center',
      // originY: 'center',
      left: 15,
      top: 15,
      name: 'group-text',
      fontSize: consts.boxLabel.fontSize * 0.8,
      fontWeight: consts.boxLabel.fontWeight,
    })

    return new fabric.Group([bg, text], {
      width: size,
      height: size,
      left: 0,
      top: 0,
      originX: 'center',
      originY: 'center',
      name: 'group'
    })
  }

  // noinspection JSMethodCanBeStatic
  private makeLabel (width: number, height: number, labelPos: string): fabric.Group {
    // let the label be half the length of the shortest box side
    const labelLength = Math.min(width, height) / 2

    // let the label thickness be at least 16 pixels but not less than 8 percent of the shortest box side
    const labelThickness = Math.max(Math.min(width, height) * 0.08, 16)

    const labelOpt1: fabric.IObjectOptions = { left: 0, top: 0, width: 0, height: 0, fill: consts.labelColor,
      originX: 'center', originY: 'center' }

    const labelOpt2: fabric.IObjectOptions = { left: 0, top: 0, width: 0, height: 0, fill: consts.labelColor,
      originX: 'center', originY: 'center' }

    const borderWidth = consts.boxBorderWidth
    switch (labelPos) {
      case 'FRONT': {
        labelOpt1.left = -width / 2 + labelThickness / 2 + borderWidth
        labelOpt1.top = 0
        labelOpt1.width = labelThickness
        labelOpt1.height = labelLength
        break
      }
      case 'FRONT_RIGHT_CORNER': {
        labelOpt1.left = -width / 2 + labelThickness / 2 + borderWidth
        labelOpt1.top = -height / 2 + labelLength / 2 + borderWidth
        labelOpt1.width = labelThickness
        labelOpt1.height = labelLength
        labelOpt2.left = -width / 2 + labelLength / 2 + borderWidth
        labelOpt2.top = -height / 2 + labelThickness / 2 + borderWidth
        labelOpt2.width = labelLength
        labelOpt2.height = labelThickness
        break
      }
      case 'RIGHT': {
        labelOpt1.left = 0
        labelOpt1.top = -height / 2 + labelThickness / 2
        labelOpt1.width = labelLength
        labelOpt1.height = labelThickness
        break
      }
      case 'RIGHT_BACK_CORNER': {
        labelOpt1.left = width / 2 - labelLength / 2
        labelOpt1.top = -height / 2 + labelThickness / 2
        labelOpt1.width = labelLength
        labelOpt1.height = labelThickness
        labelOpt2.left = width / 2 - labelThickness / 2
        labelOpt2.top = -height / 2 + labelLength / 2
        labelOpt2.width = labelThickness
        labelOpt2.height = labelLength
        break
      }
      case 'BACK': {
        labelOpt1.left = width / 2 - labelThickness / 2
        labelOpt1.top = 0
        labelOpt1.width = labelThickness
        labelOpt1.height = labelLength
        break
      }
      case 'BACK_LEFT_CORNER': {
        labelOpt1.left = width / 2 - labelLength / 2
        labelOpt1.top = height / 2 - labelThickness / 2
        labelOpt1.width = labelLength
        labelOpt1.height = labelThickness
        labelOpt2.left = width / 2 - labelThickness / 2
        labelOpt2.top = height / 2 - labelLength / 2
        labelOpt2.width = labelThickness
        labelOpt2.height = labelLength
        break
      }
      case 'LEFT': {
        labelOpt1.left = 0
        labelOpt1.top = height / 2 - labelThickness / 2
        labelOpt1.width = labelLength
        labelOpt1.height = labelThickness
        break
      }
      case 'LEFT_FRONT_CORNER': {
        labelOpt1.left = -width / 2 + labelThickness / 2
        labelOpt1.top = height / 2 - labelLength / 2
        labelOpt1.width = labelThickness
        labelOpt1.height = labelLength
        labelOpt2.left = -width / 2 + labelLength / 2
        labelOpt2.top = height / 2 - labelThickness / 2
        labelOpt2.width = labelLength
        labelOpt2.height = labelThickness
      }
    }
    const label1 = new fabric.Rect(labelOpt1)
    const label2 = new fabric.Rect(labelOpt2)
    return new fabric.Group([label1, label2], { name: 'box-label' })
  }
}
